import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { ArrowLeft, ChevronDown, ChevronRight, Folder, LoaderCircle, Search } from 'lucide-react';
import { api } from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { useAuth } from '@/lib/AuthContext';
import { Telemetry } from '@/lib/telemetry';
import { startTimer, endTimer } from '@/lib/telemetry-timers';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { SharePointAccessModal } from '@/components/shared/SharePointAccessModal';
import { FullDocumentDiffViewer } from './FullDocumentDiffViewer';
import type { ExplorerSubmission, ExplorerDocument } from '@/types/api';

type ViewMode = 'all' | 'latest';

interface ModuleGroup {
  module: string;
  label: string;
  sections: SectionRow[];
}

interface SectionRow {
  ectdSection: string;
  sectionTitle: string;
  versions: Map<string, ExplorerDocument[]>;
}

interface ContextMenuState {
  x: number;
  y: number;
  doc: ExplorerDocument;
  ectdSection: string;
  seqNum: string;
}

interface ComparisonData {
  original: { doc: ExplorerDocument; text: string; html: string };
  modified: { doc: ExplorerDocument; text: string; html: string };
}

const MODULE_LABELS: Record<string, string> = {
  M1: 'Module 1 — Administrative Information',
  M2: 'Module 2 — Summaries',
  M3: 'Module 3 — Quality',
  M4: 'Module 4 — Nonclinical Study Reports',
  M5: 'Module 5 — Clinical Study Reports',
};

// A2F-652: collapse multi-file cells to keep CSR-heavy sections (e.g. 5.3.5.1
// with 30+ docs) from blowing the matrix layout. Cells with at most this many
// docs render inline as today; cells with more docs collapse behind an
// expandable "▶ N files" summary. Tunable.
const MAX_INLINE_DOCS = 3;

function getModuleLabel(module: string): string {
  return MODULE_LABELS[module] || `Module ${module}`;
}

function getVersionLabel(
  section: SectionRow,
  seqNum: string,
  allSeqs: string[],
): string | null {
  const docs = section.versions.get(seqNum);
  if (!docs || docs.length === 0) return null;

  let versionCount = 0;
  for (const seq of [...allSeqs].reverse()) {
    const seqDocs = section.versions.get(seq);
    if (seqDocs && seqDocs.length > 0) versionCount++;
    if (seq === seqNum) break;
  }

  if (versionCount === 1) {
    const earliest = [...allSeqs].reverse()[0];
    if (seqNum !== earliest) return 'NEW';
  }

  return `v${versionCount}`;
}

function VersionBadge({ label }: { label: string }) {
  const isNew = label === 'NEW';
  return (
    <span
      className={`inline-block px-2 py-0.5 text-xs font-semibold rounded ${
        isNew
          ? 'bg-brand-orange text-white'
          : 'border border-brand-orange text-brand-orange'
      }`}
    >
      {label}
    </span>
  );
}

/** Width constants for the grid columns */
const SECTION_COL_W = 280;
const SUB_COL_W = 120;

export function SubmissionExplorerPage() {
  const { activeProject } = useProject();
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState<ExplorerSubmission[]>([]);
  const [documents, setDocuments] = useState<ExplorerDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('all');
  const [search, setSearch] = useState('');
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [spoModalUrl, setSpoModalUrl] = useState<string | null>(null);
  // A2F-652: per-cell collapse state for cells with > MAX_INLINE_DOCS docs.
  // Cell key is `${ectdSection}:${seq}` so it survives cross-section/sub clicks
  // without collisions. Memory-only — no need to persist across navigations.
  const [expandedCells, setExpandedCells] = useState<Set<string>>(new Set());

  const toggleCellExpanded = useCallback((cellKey: string) => {
    setExpandedCells((prev) => {
      const next = new Set(prev);
      if (next.has(cellKey)) next.delete(cellKey);
      else next.add(cellKey);
      return next;
    });
  }, []);

  // Comparison state
  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareError, setCompareError] = useState('');
  const contextMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!activeProject) return;
    setLoading(true);

    api.documents
      .explorer(activeProject.id)
      .then((data) => {
        setSubmissions(data.submissions);
        setDocuments(data.documents);
        const modules = new Set(data.documents.map((d) => d.module));
        setExpandedModules(modules);
        Telemetry.explorerViewed({ submission_count: data.submissions.length, document_count: data.documents.length });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [activeProject]);

  const moduleGroups = useMemo<ModuleGroup[]>(() => {
    const groups = new Map<string, Map<string, SectionRow>>();

    for (const doc of documents) {
      if (!groups.has(doc.module)) {
        groups.set(doc.module, new Map());
      }
      const moduleMap = groups.get(doc.module)!;

      if (!moduleMap.has(doc.ectdSection)) {
        moduleMap.set(doc.ectdSection, {
          ectdSection: doc.ectdSection,
          sectionTitle: doc.sectionTitle,
          versions: new Map(),
        });
      }

      const row = moduleMap.get(doc.ectdSection)!;
      const existing = row.versions.get(doc.sequenceNumber) || [];
      existing.push(doc);
      row.versions.set(doc.sequenceNumber, existing);
    }

    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([module, sectionsMap]) => ({
        module,
        label: getModuleLabel(module),
        sections: Array.from(sectionsMap.values()).sort((a, b) =>
          a.ectdSection.localeCompare(b.ectdSection, undefined, { numeric: true })
        ),
      }));
  }, [documents]);

  const seqNumbers = useMemo(
    () => submissions.map((s) => s.sequenceNumber),
    [submissions],
  );

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return moduleGroups;
    const q = search.toLowerCase();
    return moduleGroups
      .map((g) => ({
        ...g,
        sections: g.sections.filter(
          (s) =>
            s.ectdSection.toLowerCase().includes(q) ||
            s.sectionTitle.toLowerCase().includes(q) ||
            g.label.toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.sections.length > 0);
  }, [moduleGroups, search]);

  const toggleModule = (module: string) => {
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(module)) next.delete(module);
      else next.add(module);
      return next;
    });
  };

  // Close context menu on click outside
  useEffect(() => {
    if (!contextMenu) return;
    function handleClick(e: MouseEvent) {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        setContextMenu(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [contextMenu]);

  // Find the same section in an earlier submission (client-side for context menu label)
  const findPreviousVersion = useCallback(
    (ectdSection: string, currentSeqNum: string): ExplorerDocument | null => {
      const currentIdx = seqNumbers.indexOf(currentSeqNum);
      if (currentIdx === -1) return null;

      for (const group of moduleGroups) {
        for (const section of group.sections) {
          if (section.ectdSection === ectdSection) {
            for (let i = currentIdx + 1; i < seqNumbers.length; i++) {
              const docs = section.versions.get(seqNumbers[i]);
              if (docs && docs.length > 0) {
                return docs.find((d) => d.fileType === 'docx') || docs[0];
              }
            }
            return null;
          }
        }
      }
      return null;
    },
    [moduleGroups, seqNumbers],
  );

  // Compare current file to its previous version via backend
  const handleCompareToPrevious = useCallback(async () => {
    if (!contextMenu || !activeProject) return;
    const isDocx = contextMenu.doc.fileType === 'docx';
    if (!isDocx) return;

    setContextMenu(null);
    setCompareLoading(true);
    setCompareError('');
    Telemetry.documentDiffStarted({ document_id: contextMenu.doc.id });
    startTimer('document_diff');

    try {
      // Ask backend to find the previous version
      const prev = await api.documents.previousVersion(contextMenu.doc.id, activeProject.id);

      // Ask backend to extract and compare both documents
      const result = await api.documents.compare(prev.documentId, contextMenu.doc.id);

      // We need doc metadata for labels — use the context menu doc for modified,
      // and create a minimal doc object for the original
      const prevDoc: ExplorerDocument = {
        ...contextMenu.doc,
        id: prev.documentId,
        sequenceNumber: prev.sequenceNumber,
      };

      setComparisonData({
        original: { doc: prevDoc, text: result.original.text, html: result.original.html },
        modified: { doc: contextMenu.doc, text: result.modified.text, html: result.modified.html },
      });
      Telemetry.documentDiffViewed({ duration_ms: endTimer('document_diff') });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to compare documents';
      setCompareError(msg);
      Telemetry.errorSwallowed({ error_type: 'diff_failure', operation: 'document_compare', screen_name: '/see/explorer', error_message: msg });
    } finally {
      setCompareLoading(false);
    }
  }, [contextMenu, activeProject]);

  // Right-click handler for file cells
  const handleCellContextMenu = useCallback(
    (e: React.MouseEvent, docs: ExplorerDocument[], ectdSection: string, seqNum: string) => {
      if (docs.length === 0) return;
      e.preventDefault();
      const doc = docs.find((d) => d.fileType === 'docx') || docs[0];
      setContextMenu({ x: e.clientX, y: e.clientY, doc, ectdSection, seqNum });
    },
    [],
  );

  // Grid column template: fixed-width section column + fixed-width submission columns
  const colCount = viewMode === 'all' ? seqNumbers.length : 1;
  const gridCols =
    viewMode === 'all'
      ? `${SECTION_COL_W}px ${seqNumbers.map(() => `${SUB_COL_W}px`).join(' ')}`
      : `${SECTION_COL_W}px 1fr`;
  const minTableWidth = SECTION_COL_W + colCount * SUB_COL_W;

  if (loading) return <LoadingSpinner message="Loading submissions..." />;

  if (documents.length === 0) {
    return (
      <div className="max-w-2xl mx-auto mt-20 text-center">
        <Folder className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-lg font-bold text-brand-charcoal mb-2">
          No Documents Found
        </h2>
        <p className="text-sm text-slate-500">
          No documents have been ingested yet. Complete the project setup to scan
          your submission folders.
        </p>
      </div>
    );
  }

  // Comparison loading state
  if (compareLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20">
        <LoaderCircle className="w-8 h-8 text-brand-orange animate-spin" />
        <p className="text-sm text-slate-500">Extracting document content...</p>
      </div>
    );
  }

  // Comparison diff view — replaces the explorer
  if (comparisonData) {
    const { original, modified } = comparisonData;
    return (
      <div className="flex flex-col h-[calc(100vh-48px)] -mx-6 -mb-6">
        {/* Top bar */}
        <div className="shrink-0 flex items-center gap-3 px-4 py-2 bg-white border-b border-slate-200">
          <button
            onClick={() => { setComparisonData(null); setCompareError(''); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Explorer
          </button>

          <span className="text-slate-300">|</span>

          <span className="flex items-center gap-1.5">
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-600">OLD</span>
            <span className="text-sm text-brand-charcoal truncate max-w-[200px]">{original.doc.sectionTitle}</span>
            <span className="text-[10px] text-slate-400">Sub {original.doc.displaySequence}</span>
          </span>

          <span className="text-slate-400 text-xs">&rarr;</span>

          <span className="flex items-center gap-1.5">
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-green-100 text-green-600">NEW</span>
            <span className="text-sm text-brand-charcoal truncate max-w-[200px]">{modified.doc.sectionTitle}</span>
            <span className="text-[10px] text-slate-400">Sub {modified.doc.displaySequence}</span>
          </span>
        </div>

        {/* Diff viewer */}
        <div className="flex-1 overflow-hidden">
          <FullDocumentDiffViewer
            originalText={original.text}
            modifiedText={modified.text}
            originalHtml={original.html}
            modifiedHtml={modified.html}
            originalLabel={`${original.doc.sectionTitle} (Sub ${original.doc.displaySequence})`}
            modifiedLabel={`${modified.doc.sectionTitle} (Sub ${modified.doc.displaySequence})`}
          />
        </div>
      </div>
    );
  }

  const openInWord = async (documentId: string, fileType?: string) => {
    Telemetry.documentOpenedInWord({ document_id: documentId, file_type: fileType ?? 'unknown' });
    try {
      const res = await api.documents.openLocal(documentId);
      if (res.status === 'redirect' && res.webUrl) {
        if (user?.authMethod === 'sso') {
          window.open(res.webUrl, '_blank');
        } else {
          setSpoModalUrl(res.webUrl);
        }
      }
    } catch { /* ignore */ }
  };

  /** Render the contents of a cell for one or more documents. */
  function renderCell(docs: ExplorerDocument[], label: string | null, cellKey: string) {
    if (!docs || docs.length === 0 || !label) {
      return <span className="text-sm text-slate-300">&mdash;</span>;
    }

    // Single document
    if (docs.length === 1) {
      return (
        <div
          className="cursor-pointer hover:bg-orange-50 flex items-center justify-center w-full h-full"
          onClick={() => openInWord(docs[0].id)}
        >
          <VersionBadge label={label} />
        </div>
      );
    }

    // A2F-652: collapse cells with many docs behind an expandable summary.
    // 2..MAX_INLINE_DOCS render inline (compact stack); MAX_INLINE_DOCS+1
    // and beyond render a "▶ N files" toggle that expands to the same
    // stacked view on click.
    const isExpanded = expandedCells.has(cellKey);
    const collapsible = docs.length > MAX_INLINE_DOCS;

    if (collapsible && !isExpanded) {
      return (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); toggleCellExpanded(cellKey); }}
          className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 bg-white hover:bg-orange-50 text-xs font-medium text-brand-charcoal"
          title={`Expand to show ${docs.length} files`}
        >
          <ChevronRight className="w-3 h-3 text-slate-400" />
          {docs.length} files
        </button>
      );
    }

    // Inline stack (≤ MAX_INLINE_DOCS) OR expanded view (> MAX_INLINE_DOCS).
    // Right-click context menu still lands on individual docs in this state.
    return (
      <div className="flex flex-col gap-1.5 items-center w-full">
        {collapsible && isExpanded && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); toggleCellExpanded(cellKey); }}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-slate-500 hover:text-brand-charcoal"
            title="Collapse"
          >
            <ChevronDown className="w-3 h-3" />
            Collapse {docs.length}
          </button>
        )}
        {docs.map((doc) => (
          <div
            key={doc.id}
            className="cursor-pointer hover:bg-orange-50 rounded px-1 py-0.5 text-center"
            onClick={() => openInWord(doc.id)}
          >
            <VersionBadge label={label} />
            <p className="text-[10px] text-slate-400 truncate max-w-[100px]" title={doc.fileName}>
              {doc.fileName}
            </p>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-full">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <h1 className="text-xl font-bold text-brand-charcoal">Submission Explorer</h1>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
          <button
            onClick={() => setViewMode('all')}
            className={`px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
              viewMode === 'all'
                ? 'bg-brand-orange text-white'
                : 'bg-white text-slate-500 hover:bg-slate-50'
            }`}
          >
            All Submissions
          </button>
          <button
            onClick={() => setViewMode('latest')}
            className={`px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
              viewMode === 'latest'
                ? 'bg-brand-orange text-white'
                : 'bg-white text-slate-500 hover:bg-slate-50'
            }`}
          >
            Latest Only
          </button>
        </div>
        <div className="flex-1" />
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sections or modules..."
            className="pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg w-64 focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange"
          />
        </div>
      </div>

      {/* Compare error banner */}
      {compareError && (
        <div className="mb-4 px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
          {compareError}
        </div>
      )}

      {/* Scrollable Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <div style={{ minWidth: minTableWidth }}>
          {/* Table Header */}
          <div
            className="grid border-b border-slate-200 bg-slate-50/50 sticky top-0 z-10"
            style={{ gridTemplateColumns: gridCols }}
          >
            <div className="px-4 py-3 sticky left-0 bg-slate-50 z-20 border-r border-slate-200">
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                eCTD Section
              </p>
            </div>
            {viewMode === 'all' ? (
              seqNumbers.map((seq) => {
                const sub = submissions.find((s) => s.sequenceNumber === seq);
                return (
                  <div key={seq} className="px-3 py-3 border-l border-slate-100">
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                      Sub {sub?.displaySequence ?? parseInt(seq, 10)}
                    </p>
                    {sub?.submissionDate && (
                      <p className="text-[10px] text-slate-400 whitespace-nowrap">
                        {sub.submissionDate}
                      </p>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="px-4 py-3 border-l border-slate-100">
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  Latest Version
                </p>
              </div>
            )}
          </div>

          {/* Table Body */}
          {filteredGroups.map((group) => (
            <div key={group.module}>
              {/* Module Header Row */}
              <div
                className="grid border-b border-slate-100 bg-slate-50/30 cursor-pointer hover:bg-slate-50"
                style={{ gridTemplateColumns: gridCols }}
                onClick={() => toggleModule(group.module)}
              >
                <div className="px-4 py-3 flex items-center gap-2 sticky left-0 bg-slate-50/30 z-10 border-r border-slate-200">
                  {expandedModules.has(group.module) ? (
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  )}
                  <Folder className="w-4 h-4 text-brand-orange shrink-0" />
                  <span className="text-sm font-semibold text-brand-charcoal truncate">
                    {group.label}
                  </span>
                  <span className="text-[10px] text-slate-400 ml-1 whitespace-nowrap">
                    ({group.sections.length})
                  </span>
                </div>
                {Array.from({ length: colCount }).map((_, i) => (
                  <div key={i} className="border-l border-slate-100" />
                ))}
              </div>

              {/* Section Rows */}
              {expandedModules.has(group.module) &&
                group.sections.map((section) => (
                  <div
                    key={section.ectdSection}
                    className="grid border-b border-slate-100 hover:bg-orange-50/30"
                    style={{ gridTemplateColumns: gridCols }}
                  >
                    <div className="px-4 py-3 pl-12 sticky left-0 bg-white z-10 border-r border-slate-200">
                      <p className="text-[10px] text-slate-400">{section.ectdSection}</p>
                      <p className="text-sm text-brand-charcoal truncate">{section.sectionTitle}</p>
                    </div>

                    {viewMode === 'all' ? (
                      seqNumbers.map((seq) => {
                        const label = getVersionLabel(section, seq, seqNumbers);
                        const docs = section.versions.get(seq) || [];
                        return (
                          <div
                            key={seq}
                            className="px-3 py-3 border-l border-slate-100 flex items-center justify-center"
                            onContextMenu={(e) =>
                              handleCellContextMenu(e, docs, section.ectdSection, seq)
                            }
                          >
                            {renderCell(docs, label, `${section.ectdSection}:${seq}`)}
                          </div>
                        );
                      })
                    ) : (
                      (() => {
                        const latestSeq = seqNumbers.find((s) => {
                          const d = section.versions.get(s);
                          return d && d.length > 0;
                        });
                        const docs = latestSeq ? section.versions.get(latestSeq) || [] : [];
                        const label = latestSeq
                          ? getVersionLabel(section, latestSeq, seqNumbers)
                          : null;
                        return (
                          <div
                            className="px-4 py-3 border-l border-slate-100 flex items-center"
                            onContextMenu={(e) => {
                              if (latestSeq) {
                                handleCellContextMenu(e, docs, section.ectdSection, latestSeq);
                              }
                            }}
                          >
                            {renderCell(docs, label, `${section.ectdSection}:latest`)}
                          </div>
                        );
                      })()
                    )}
                  </div>
                ))}
            </div>
          ))}

          {filteredGroups.length === 0 && (
            <div className="px-4 py-12 text-center text-sm text-slate-400">
              {search ? 'No matching sections.' : 'No documents found.'}
            </div>
          )}
        </div>
      </div>

      {/* Right-click context menu */}
      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="fixed z-50 bg-white rounded-lg border border-slate-200 shadow-lg py-1 min-w-[240px]"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          {(() => {
            const prevDoc = findPreviousVersion(contextMenu.ectdSection, contextMenu.seqNum);
            const isDocx = contextMenu.doc.fileType === 'docx';
            const canCompare = prevDoc && isDocx && prevDoc.fileType === 'docx';

            return (
              <button
                className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                  canCompare
                    ? 'text-brand-charcoal hover:bg-orange-50'
                    : 'text-slate-400 cursor-not-allowed'
                }`}
                onClick={canCompare ? handleCompareToPrevious : undefined}
                disabled={!canCompare}
              >
                {canCompare
                  ? `Compare to previous version (Sub ${prevDoc!.displaySequence})`
                  : !isDocx
                    ? 'Compare not available for PDFs'
                    : 'No previous version to compare'}
              </button>
            );
          })()}
        </div>
      )}
      {spoModalUrl && (
        <SharePointAccessModal
          webUrl={spoModalUrl}
          onClose={() => setSpoModalUrl(null)}
        />
      )}
    </div>
  );
}
